import { useMemo, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View, type StyleProp, type TextStyle } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';

import { useTheme } from '@/contexts/ThemeContext';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';

type Props = {
  style?: StyleProp<TextStyle>;
  /** Αν true, δείχνει μόνο τον τίτλο — πάτημα ανοίγει το πλήρες κείμενο σε modal. */
  compact?: boolean;
};

/** Μικρή νομική σημείωση — η πλατφόρμα δεν παρέχει νομικές υπηρεσίες. */
export function DisclaimerNote({ style, compact = false }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [open, setOpen] = useState(false);

  if (compact) {
    return (
      <>
        <Pressable onPress={() => setOpen(true)} accessibilityRole="button">
          <Text style={[styles.noteCompact, style]}>{t('disclaimerNote.title')}</Text>
        </Pressable>
        <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
          <Pressable style={styles.overlay} onPress={() => setOpen(false)} accessibilityRole="button">
            <Pressable style={[styles.sheet, { paddingBottom: insets.bottom + spacing.lg }]} onPress={() => {}}>
              <View style={styles.handle} />
              <View style={styles.sheetHeader}>
                <Text style={styles.sheetTitle}>{t('disclaimerNote.title')}</Text>
                <Pressable onPress={() => setOpen(false)} hitSlop={12} accessibilityRole="button">
                  <Ionicons name="close" size={22} color={colors.textMuted} />
                </Pressable>
              </View>
              <Text style={styles.sheetBody}>{t('disclaimerNote.text')}</Text>
            </Pressable>
          </Pressable>
        </Modal>
      </>
    );
  }

  return (
    <Text style={[styles.note, style]}>
      {t('disclaimerNote.text')}
    </Text>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    note: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      lineHeight: 18,
      textAlign: 'center',
      paddingHorizontal: spacing.sm,
    },
    noteCompact: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      fontWeight: '600',
      textAlign: 'center',
      textDecorationLine: 'underline',
      paddingHorizontal: spacing.sm,
    },
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'flex-end',
    },
    sheet: {
      backgroundColor: colors.surface,
      borderTopLeftRadius: radius.xl,
      borderTopRightRadius: radius.xl,
      padding: spacing.lg,
      gap: spacing.md,
    },
    handle: {
      width: 40, height: 4, borderRadius: 2,
      backgroundColor: colors.border, alignSelf: 'center',
    },
    sheetHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    sheetTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    sheetBody: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 21 },
  });
}
